import { Stack, Typography, IconButton, Box } from '@mui/material';
import { useTBContext } from "../../Contexts/useTBStates";
import { useEffect, useState } from 'react';
import { StyledAutocompleteInput } from '../../../shared/Components/StyledAutocompleteInput';
import { StyledIconButton } from '../../../shared/Components/StyledIconButton';
import DeleteIcon from '@mui/icons-material/Delete';

interface MixedPlayFormatItem {
    name: string;
    playFormat: string | null;
}

const mixedPlayFormatsTemplate: MixedPlayFormatItem[] = [
    {
        name: '1-18',
        playFormat: '18'
    },
]

const isMixedPlayFormatItem = (value: unknown): value is MixedPlayFormatItem => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const name = (value as { name?: unknown }).name;
    const playFormat = (value as { playFormat?: unknown }).playFormat;
    return typeof name === "string" && (typeof playFormat === "string" || playFormat === null);
};

const getCurrentMixedPlayFormats = (viewEvent: any, roundValue: number): MixedPlayFormatItem[] => {
    const maybeFormats = viewEvent?.rounds?.[roundValue - 1]?.mixedPlayFormats;
    if (!Array.isArray(maybeFormats) || maybeFormats.length === 0) return mixedPlayFormatsTemplate;
    return maybeFormats.every(isMixedPlayFormatItem) ? maybeFormats : mixedPlayFormatsTemplate;
};

export const MixedPlayFormatsLayout = () => {

    const { viewEvent, roundValue, editMode, appTheme, factor, setViewEvent, optionsLoaded } = useTBContext();

    const playFormatsSet = optionsLoaded.find((set: any) => set?.keyName === 'PlayFormats') as { options?: any[] } | undefined;
    const pfOptions = Array.isArray(playFormatsSet?.options) ? playFormatsSet.options : [];

    const [mixedPlayFormats, setMixedPlayFormats] = useState<MixedPlayFormatItem[]>(getCurrentMixedPlayFormats(viewEvent, roundValue));
    const [arrayLength, setArrayLength] = useState<number>(getCurrentMixedPlayFormats(viewEvent, roundValue).length);

    useEffect(() => {
        const currentFormats = getCurrentMixedPlayFormats(viewEvent, roundValue);
        setMixedPlayFormats(currentFormats);
        setArrayLength(currentFormats.length);
    }, [viewEvent, roundValue]);

    const updateViewEvent = (nextMixedPlayFormats: MixedPlayFormatItem[]) => {
        if (!viewEvent?.rounds?.[roundValue - 1]) return;

        const newViewEvent = JSON.parse(JSON.stringify(viewEvent));
        newViewEvent.rounds[roundValue - 1].mixedPlayFormats = nextMixedPlayFormats;
        setViewEvent(JSON.parse(JSON.stringify(newViewEvent)));
    }

    const handleMixedPlaySpacingChange = (roundIndex: number, holeSpacing: number) => {
        if (!viewEvent?.rounds?.[roundIndex]) return;

        const updatedRounds = JSON.parse(JSON.stringify(viewEvent.rounds));
        const updatedMixedPlayFormats: MixedPlayFormatItem[] = [];
        const rowQty = 18 / holeSpacing;

        for (let i = 0; i < rowQty; i++) {
            updatedMixedPlayFormats.push({
                name: `${i*holeSpacing + 1}-${(i+1)*holeSpacing}`,
                playFormat: null
            });
        }

        updatedRounds[roundIndex].mixedPlayFormats = updatedMixedPlayFormats;
        setViewEvent({...viewEvent, rounds: updatedRounds});
    }

    const handleMixedPlayFormatChange = (index: number, type: string | null) => {
        if (!type) return;

        const updatedMixedPlayFormats = JSON.parse(JSON.stringify(mixedPlayFormats)) as MixedPlayFormatItem[];
        updatedMixedPlayFormats[index].playFormat = type;

        setMixedPlayFormats(updatedMixedPlayFormats);
        updateViewEvent(updatedMixedPlayFormats);
    }

    const handleDelete = (index: number) => {
        const updatedMixedPlayFormats = JSON.parse(JSON.stringify(mixedPlayFormats));
        updatedMixedPlayFormats.splice(index, 1);
        setMixedPlayFormats(updatedMixedPlayFormats);
        updateViewEvent(updatedMixedPlayFormats);
    }

    const splitButtonSx = (active: boolean) => ({
        width: 28 * factor,
        height: 28 * factor,
        fontSize: 12,
        backgroundColor: active ? 'primary.light' : 'action.hover',
        color: active ? 'secondary.main' : 'primary.main',
    });

    return (
        <Stack direction="column" spacing={1.25} sx={{ width: '100%', minWidth: 0, pt: 0.5 }}>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                    width: '100%',
                    minWidth: 0,
                    pb: 1,
                    borderBottom: `1px dashed ${appTheme.palette.divider}`,
                }}
            >
                <Typography
                    flex={2}
                    sx={{ fontWeight: 500, fontSize: 14 * factor, minWidth: 0 }}
                >
                    Hole Splits:
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 3, minWidth: 0 }}>
                    <IconButton
                        onClick={() => handleMixedPlaySpacingChange(roundValue - 1, 3)}
                        color="primary"
                        disabled={!editMode}
                        sx={splitButtonSx(arrayLength === 6)}
                    >
                        3
                    </IconButton>
                    <IconButton
                        onClick={() => handleMixedPlaySpacingChange(roundValue - 1, 6)}
                        color="primary"
                        disabled={!editMode}
                        sx={splitButtonSx(arrayLength === 3)}
                    >
                        6
                    </IconButton>
                    <IconButton
                        onClick={() => handleMixedPlaySpacingChange(roundValue - 1, 9)}
                        color="primary"
                        disabled={!editMode}
                        sx={splitButtonSx(arrayLength === 2)}
                    >
                        9
                    </IconButton>
                </Stack>
            </Stack>

            {mixedPlayFormats.map((mixedPlayFormat: MixedPlayFormatItem, index: number) => (
                <Stack
                    key={`${mixedPlayFormat.name}-${index}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ width: '100%', minWidth: 0 }}
                >
                    <Typography
                        flex={2}
                        sx={{
                            fontWeight: 500,
                            fontSize: 14 * factor,
                            minWidth: 0,
                            color: 'text.primary',
                        }}
                    >
                        {mixedPlayFormat.name}:
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ flex: 3, minWidth: 0 }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <StyledAutocompleteInput
                                label="Play Format"
                                factor={factor}
                                appTheme={appTheme}
                                value={pfOptions.find((option: any) =>
                                    option.type === mixedPlayFormat.playFormat) || (mixedPlayFormat.playFormat ? { type: mixedPlayFormat.playFormat } : null)}
                                options={pfOptions}
                                getOptionLabel={(option: any) => option.type}
                                isOptionEqualToValue={(option: any, value: any) => option?.type === value?.type}
                                disabled={!editMode}
                                width="100%"
                                onChange={(_event, value) => handleMixedPlayFormatChange(index, value?.type ?? null)}
                            />
                        </Box>
                        {editMode ? (
                            <StyledIconButton
                                appTheme={appTheme}
                                icon={<DeleteIcon sx={{ fontSize: 16 * factor }} />}
                                label=""
                                factor={factor}
                                color="error"
                                disabled={!editMode}
                                width={25 * factor}
                                height={25 * factor}
                                onClick={() => handleDelete(index)}
                            />
                        ) : null}
                    </Stack>
                </Stack>
            ))}
        </Stack>
    )
}
